/**
 * The browser-facing contract of the signed-in snapshot form's client-record routes (docs/43
 * slice 4b, crossmarc/steward-app#2699):
 *
 *   GET  /intake/{firmSlug}/client-record/snapshot
 *   POST /intake/{firmSlug}/client-record/snapshot/answers
 *   POST /intake/{firmSlug}/client-record/snapshot/send
 *
 * Constants only, so the SPA and the façade agree on one spelling. The façade
 * (`handlers/clientRecordForm.ts`) is the authority on what each route does.
 */

/**
 * The onboarding form's three routes (docs/43 slice 4c), beside the snapshot's:
 *
 *   GET  /intake/{firmSlug}/client-record/onboarding
 *   POST /intake/{firmSlug}/client-record/onboarding/answers
 *   POST /intake/{firmSlug}/client-record/onboarding/send
 *
 * Form-specific rules, so they are not the snapshot's: no sharing consent and no resume position
 * (appcore refuses `resume` for any form but the snapshot), writes are refused once the form is
 * closed (409 `ONBOARDING_FORM_CLOSED`), and the advisor's information requests are a row of their
 * own that is saved by itself.
 */
export const CLIENT_RECORD_ONBOARDING_ANSWERS_REQUEST_KEYS = ['expectedVersion', 'answers', 'lists'] as const;

/** The keys the answers route's strict body schema accepts. The façade's test holds its schema to this list. */
export const CLIENT_RECORD_ANSWERS_REQUEST_KEYS = [
  'expectedVersion', 'resume', 'answers', 'lists', 'sharingConsent', 'invitationCode',
] as const;
export type ClientRecordAnswersRequestKey = (typeof CLIENT_RECORD_ANSWERS_REQUEST_KEYS)[number];

/** The façade's 409 while the writer has not yet made (or bound) the client's record. The browser asks again shortly. */
export const CLIENT_RECORD_NOT_READY_CODE = 'CLIENT_RECORD_NOT_READY';
/** appcore's 409 for a save made against an older version of the answers, relayed unchanged. */
export const CLIENT_RECORD_STALE_SAVE_CODE = 'stale_save';
/** appcore's 409 for a snapshot send with no sharing consent on the record, relayed unchanged. */
export const SNAPSHOT_CONSENT_REQUIRED_CODE = 'SNAPSHOT_CONSENT_REQUIRED';
/**
 * The façade's own 409 for a save whose sharing consent it HELD BACK (no Terms acceptance on
 * record) and which appcore then refused for want of a consent stamp. Distinct from
 * {@link SNAPSHOT_CONSENT_REQUIRED_CODE}: that one, on a save that carried a consent, means the
 * stamp is on its way and a retry may land; this one means the consent was never sent, so a retry
 * cannot (the person must accept the Terms first).
 */
export const SNAPSHOT_CONSENT_HELD_CODE = 'SNAPSHOT_CONSENT_HELD';
/** appcore's 409 for a second send of the same form, relayed unchanged. */
export const CLIENT_FORM_ALREADY_SENT_CODE = 'CLIENT_FORM_ALREADY_SENT';

/**
 * The version string the façade stamps on a sharing consent it sends the writer. The sentence is
 * composed by the writer, so this names which wording the person saw, not the wording.
 */
export const CLIENT_SHARING_CONSENT_VERSION = 'v1';
